import { message } from "antd";
import axios, { CanceledError } from "axios";
import { config } from "config";

// Existing authenticated browser sessions are included; phone entry is not login.
// Keep this client separate from the app's token-authenticated client.
const webClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 65000,
  withCredentials: true,
});

const flattenErrors = (value, prefix = "") => {
  if (typeof value === "string")
    return [prefix ? `${prefix}: ${value}` : value];
  if (Array.isArray(value))
    return value.flatMap((item) => flattenErrors(item, prefix));
  if (value && typeof value === "object") {
    if (value.mn || value.en)
      return flattenErrors(value.mn || value.en, prefix);
    return Object.entries(value).flatMap(([key, item]) =>
      flattenErrors(item, prefix ? `${prefix}.${key}` : key)
    );
  }
  return [];
};

const request = async (send, { signal, quiet = false } = {}) => {
  try {
    const response = await send();
    if (signal?.aborted) throw new CanceledError();
    if (
      response.status < 200 ||
      response.status >= 300 ||
      response.data?.code !== 1
    ) {
      const error = new Error(
        flattenErrors(response.data?.msg).join("\n") ||
          "Хүсэлт амжилтгүй боллоо."
      );
      error.fields = response.data?.msg;
      throw error;
    }
    if (!quiet) {
      const notices = {
        pending: ["info", "Төлбөр хүлээгдэж байна."],
        paid: ["success", "Тасалбарын худалдан авалт амжилттай боллоо."],
        expired: ["warning", "Төлбөрийн хугацаа дууссан байна."],
        failed: ["error", "Төлбөр амжилтгүй боллоо."],
      };
      const [type, fallback] = notices[response.data?.data?.status] || [
        "info",
        "Хүсэлт амжилттай боллоо.",
      ];
      message[type](flattenErrors(response.data?.msg).join("\n") || fallback);
    }
    return response.data;
  } catch (error) {
    if (signal?.aborted || error.code === "ERR_CANCELED") throw error;
    const text =
      flattenErrors(error.response?.data?.msg).join("\n") ||
      error.message ||
      "Сервертэй холбогдож чадсангүй.";
    if (!quiet) message.error(text);
    const failure = new Error(text);
    failure.fields = error.response?.data?.msg || error.fields;
    throw failure;
  }
};

const post = async (path, body, options = {}) => {
  const config = options.signal ? { signal: options.signal } : {};
  const csrf = await request(() => webClient.get("web/megawin/csrf/", config), {
    ...options,
    quiet: true,
  });
  if (!csrf.csrfToken) throw new Error("CSRF токен авч чадсангүй.");
  if (options.signal?.aborted) throw new CanceledError();
  return request(
    () =>
      webClient.post(path, body, {
        ...config,
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrf.csrfToken,
        },
      }),
    options
  );
};

export const megawinApi = {
  getSections() {
    return request(() => webClient.get("web/megawin/sections/"));
  },
  getCustomer(phone, options = {}) {
    return post(
      "web/megawin/customer/",
      { phone },
      { quiet: true, ...options }
    );
  },
  checkout(items, phone, options) {
    return post("web/megawin/checkout/", { items, phone, qpay: true }, options);
  },
  checkPayment(checkoutId, options) {
    return post("web/megawin/check-payment/", { checkoutId }, options);
  },
  checkTickets(checkoutId, phone, options = {}) {
    return post(
      "web/megawin/check-tickets/",
      { checkoutId, phone },
      { quiet: true, ...options }
    );
  },
};
